import { useRef, useEffect, useState, useCallback } from 'react';
import { PulseUser } from './usePulseSocket';

function click(ctx: AudioContext, when: number) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.frequency.value = 1200 + Math.random() * 200; // slight variance like rain
  osc.type = 'sine';
  gain.gain.setValueAtTime(0.018, when);
  gain.gain.exponentialRampToValueAtTime(0.001, when + 0.04);
  osc.start(when);
  osc.stop(when + 0.04);
}

export function useRainClick(users: Map<string, PulseUser>) {
  const [enabled, setEnabled] = useState(() => {
    return localStorage.getItem('pulse-sound') === 'on';
  });
  const ctxRef = useRef<AudioContext | null>(null);
  const prevRef = useRef<Map<string, number>>(new Map());

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      localStorage.setItem('pulse-sound', next ? 'on' : 'off');
      // Resume or create AudioContext on user gesture
      if (next) {
        if (!ctxRef.current) ctxRef.current = new AudioContext();
        else ctxRef.current.resume();
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const ctx = ctxRef.current || new AudioContext();
    ctxRef.current = ctx;

    let totalNew = 0;
    for (const [id, user] of users) {
      const prevLen = prevRef.current.get(id) ?? 0;
      if (user.buffer.length > prevLen) {
        totalNew += user.buffer.length - prevLen;
      }
    }

    // Cap at a few clicks to avoid audio spam on paste
    const clicks = Math.min(totalNew, 4);
    for (let i = 0; i < clicks; i++) {
      click(ctx, ctx.currentTime + i * 0.015);
    }

    // Snapshot current buffer lengths
    const next = new Map<string, number>();
    for (const [id, user] of users) {
      next.set(id, user.buffer.length);
    }
    prevRef.current = next;
  }, [users, enabled]);

  return { enabled, toggle };
}
